import { Link } from "react-router";
import { ArrowLeft, Tag, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function CreateTestLabelDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Create New Test Label in Mask</h1>
              <p className="text-xl text-gray-600">Step-by-step guide</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              <a href="/docs/glossary#test-label" className="text-blue-600 hover:underline">
                Test labels
              </a>{" "}
              define specific measurement points and conditions within a{" "}
              <a href="/docs/glossary#mask" className="text-blue-600 hover:underline">
                mask
              </a>
              . They specify what should be measured, how it should be measured, and under what
              conditions. Creating test labels is essential for organizing your measurement
              workflow.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm">
                <li>
                  • Your role is{" "}
                  <a
                    href="/docs/glossary#project-manager"
                    className="text-blue-600 hover:underline"
                  >
                    project manager
                  </a>{" "}
                  or Admin
                </li>
                <li>
                  • You must have an existing project with a{" "}
                  <a href="/docs/glossary#mask" className="text-blue-600 hover:underline">
                    Mask
                  </a>
                </li>
                <li>
                  • At least one{" "}
                  <a href="/docs/glossary#spec" className="text-blue-600 hover:underline">
                    spec list
                  </a>{" "}
                  associated to your Mask
                </li>
                <li>• Understanding of the measurement parameters needed</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Step-by-Step Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Navigate to Test Labels</h3>
                <p className="text-gray-700 mb-2">
                  From the main navigation, go to the Test Labels page to manage all test labels
                  across your projects.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/test-labels">Go to Test Labels</Link>
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Create New Test Label</h3>
                <p className="text-gray-700">
                  Click the "Create New Test Label" button to open the test label creation form.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Define Test Label Properties</h3>
                <p className="text-gray-700 mb-2">Configure the test label details:</p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Name:</strong> Unique identifier for the test label
                  </li>
                  <li>
                    • <strong>Description:</strong> Detailed description of what this test measures
                  </li>
                  <li>
                    • <strong>Mask:</strong> Select the mask to which this test label applies. You
                    can use the search field for faster lookup{" "}
                  </li>
                  <li>
                    • <strong>Spec List:</strong> This shows all the spec lists that are associated
                    with the selected mask. The selection of specs in this test label will be
                    limited to the specs in this spec list.
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                4
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Add specs</h3>
                <p className="text-gray-700 mb-2">
                  Choose which specs from the selected spec list should be included in this test
                  label. You can add or remove specs by clicking the checkbox to the left side of
                  the spec.
                </p>
                <p className="text-gray-700 mb-2">
                  You can search for a specific spec by name using the search field, or filter the
                  specs per class using the Device class filter.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                5
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Save Test Label</h3>
                <p className="text-gray-700">
                  Review all settings and click "Create" to save the test label. It will now be
                  available for measurement requests.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">After creating your test label, you can:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button asChild variant="outline" size="sm">
                <Link to="/docs/create-measurement-request">Create Measurement Request</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/test-labels">View All Test Labels</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
